/* ==========================================================================
   KRAXIE — global.js
   No framework, no dependencies. Custom elements + a small pub/sub bus.
   Loaded with defer; nothing here blocks first paint.
   ========================================================================== */

(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.remove('no-js');

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Event bus ---------------------------------------------------------- */

  const bus = {
    map: new Map(),
    on(evt, fn) {
      if (!this.map.has(evt)) this.map.set(evt, new Set());
      this.map.get(evt).add(fn);
      return () => this.map.get(evt).delete(fn);
    },
    emit(evt, data) {
      (this.map.get(evt) || []).forEach((fn) => fn(data));
    }
  };

  /* --- Motion ------------------------------------------------------------- */
  /* If the browser can run scroll-driven animations, CSS owns every reveal
     and this file never touches the DOM for them. Otherwise we flag the
     document and drive the same states with one shared observer. */

  const nativeScrollTimeline =
    CSS.supports('animation-timeline: view()') && !reduced;

  if (!nativeScrollTimeline && !reduced) {
    root.classList.add('motion-js');

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    const bind = (scope) => {
      scope.querySelectorAll('[data-reveal]').forEach((el) => {
        if (el.dataset.revealBound) return;
        el.dataset.revealBound = '1';
        if (el.dataset.reveal === 'stagger') {
          [...el.children].forEach((child, i) =>
            child.style.setProperty('--i', i)
          );
        }

        /* Anything already inside the first screen is shown at once. A short
           hero must never leave a blank band waiting for a scroll that the
           visitor has no reason to make. */
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add('is-in');
          return;
        }

        io.observe(el);
      });
    };

    bind(document);
    document.addEventListener('shopify:section:load', (e) => bind(e.target));
  }

  /* Chrome sheen — fires once, when the wordmark first lands on screen. */
  if (!reduced) {
    const lit = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-lit');
          lit.unobserve(entry.target);
        });
      },
      { threshold: 0.6 }
    );
    document
      .querySelectorAll('.chrome-text')
      .forEach((el) => lit.observe(el));
  }

  /* --- Scroll lock -------------------------------------------------------- */
  /* Position-fixed lock so iOS Safari doesn't scroll the page behind drawers. */

  let lockCount = 0;
  let lockY = 0;

  const lockScroll = () => {
    if (lockCount++ > 0) return;
    lockY = window.scrollY;
    document.body.style.top = `-${lockY}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.setAttribute('data-scroll-locked', '');
  };

  const unlockScroll = () => {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.removeAttribute('data-scroll-locked');
    window.scrollTo(0, lockY);
  };

  /* --- Focus trap --------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container) => {
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = [...container.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  };

  /* --- Drawer ------------------------------------------------------------- */
  /* <kraxie-drawer id="cart-drawer"> … </kraxie-drawer>
     Openers: any element with [data-drawer-open="cart-drawer"] */

  class KraxieDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');
      this.releaseFocus = null;
      this.opener = null;

      /* Delegated: the cart body is replaced wholesale after every mutation,
         so per-node listeners would be lost on the first update. */
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.overlay?.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open(opener) {
      if (this.hasAttribute('open')) return;
      this.opener = opener || document.activeElement;
      this.setAttribute('open', '');
      this.panel?.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.releaseFocus = trapFocus(this);
      requestAnimationFrame(() => {
        (this.querySelector('[data-drawer-focus]') ||
          this.querySelector(FOCUSABLE))?.focus();
      });
      bus.emit('drawer:open', this.id);
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      this.panel?.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      this.releaseFocus?.();
      this.opener?.focus();
      bus.emit('drawer:close', this.id);
    }

    toggle(opener) {
      this.hasAttribute('open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('kraxie-drawer', KraxieDrawer);

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.drawerOpen);
    if (!target) return;
    e.preventDefault();
    target.toggle(trigger);
  });

  /* --- Header ------------------------------------------------------------- */
  /* Hides on scroll down past the hero, returns on scroll up. Switches to the
     solid state the moment it leaves a transparent hero. */

  class KraxieHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header') || this;
      this.last = window.scrollY;
      this.threshold = 80;
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      bus.on('drawer:open', () => this.header.setAttribute('data-state', 'shown'));
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const y = window.scrollY;

      if (document.body.hasAttribute('data-scroll-locked')) {
        this.last = y;
        return;
      }

      /* Pinned by default. Hiding is opt-in via [data-hide-on-scroll],
         because a header that vanishes costs more than the space it frees. */
      if (this.hasAttribute('data-hide-on-scroll')) {
        const down = y > this.last && y > this.threshold * 3;
        this.header.setAttribute('data-state', down ? 'hidden' : 'shown');
      }

      this.header.toggleAttribute('data-scrolled', y > this.threshold);
      this.last = y;
    }
  }

  customElements.define('kraxie-header', KraxieHeader);

  /* --- Quantity ----------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.step === 'up' ? 1 : -1;
          const min = Number(this.input.min || 0);
          const next = Math.max(min, Number(this.input.value) + step);
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* --- Cart --------------------------------------------------------------- */
  /* Every mutation goes through here so the drawer, the badge and any
     future cart UI stay in sync from one server response. */

  const cart = {
    sections: ['cart-drawer', 'cart-badge'],

    routes: window.Kraxie?.routes || {
      cartAdd: '/cart/add',
      cartChange: '/cart/change',
      cartUpdate: '/cart/update'
    },

    async request(url, body) {
      const res = await fetch(`${url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ ...body, sections: this.sections.join(',') })
      });
      const data = await res.json();
      if (!res.ok) throw data;
      return data;
    },

    async add(formData, opts = {}) {
      const body = {};
      for (const [key, value] of formData.entries()) {
        if (key === 'id') body.id = value;
        else if (key === 'quantity') body.quantity = Number(value);
        else if (key.startsWith('properties[')) {
          body.properties = body.properties || {};
          body.properties[key.slice(11, -1)] = value;
        } else if (key === 'selling_plan') body.selling_plan = value;
      }
      const data = await this.request(this.routes.cartAdd, {
        items: [body]
      });
      this.render(data.sections);
      bus.emit('cart:add', data);
      if (opts.openDrawer !== false) {
        document.getElementById('cart-drawer')?.open();
      }
      return data;
    },

        async change(line, quantity) {
      let data = await this.request(this.routes.cartChange, {
        line,
        quantity
      });
      /* An empty cart must not keep a discount code from earlier. */
      if (data.item_count === 0) data = await this.clearDiscount(data);
      this.render(data.sections);
      bus.emit('cart:change', data);
      return data;
    },

    async clearDiscount(fallback) {
      try {
        document.cookie = 'discount_code=; Max-Age=0; path=/';
        return await this.request(this.routes.cartUpdate, { discount: '' });
      } catch (err) {
        return fallback;
      }
    },

    /* Re-reads the drawer and the header count, e.g. after the browser's
       back button restores an old copy of the page. */
    async refresh() {
      try {
        const root = window.Kraxie?.routes?.root || '/';
        const res = await fetch(`${root}?sections=${this.sections.join(',')}`);
        if (res.ok) this.render(await res.json());
      } catch (err) {
        /* leave the page as it is */
      }
    },

    render(sections) {
      if (!sections) return;
      Object.values(sections).forEach((html) => {
        if (!html) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        /* One section can hold several mount points (the badge has a flat icon
           and a 3D-cart overlay). Refresh every one, wherever it appears. */
        parsed.querySelectorAll('[data-cart-section]').forEach((source) => {
          const key = source.getAttribute('data-cart-section');
          document.querySelectorAll(`[data-cart-section="${key}"]`).forEach((target) => {
            target.innerHTML = source.innerHTML;
          });
        });
      });
    }
  };

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) cart.refresh();
  });

  /* Line quantity + remove inside the drawer, delegated so re-renders work */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-line-qty]');
    if (!input) return;
    input.closest('[data-cart-line]')?.setAttribute('data-busy', '');
    cart
      .change(Number(input.dataset.line), Number(input.value))
      .catch(() => window.location.reload());
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (!remove) return;
    e.preventDefault();
    remove.closest('[data-cart-line]')?.setAttribute('data-busy', '');
    cart.change(Number(remove.dataset.line), 0).catch(() => window.location.reload());
  });

  /* --- Product form ------------------------------------------------------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-form-error]');
      this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }

        async onSubmit(e) {
      e.preventDefault();
      /* aria-disabled="false" still counts as the attribute being present, so
         read its value, and ignore a second tap while the first is in flight. */
      if (this.busy) return;
      if (this.button?.disabled || this.button?.getAttribute('aria-disabled') === 'true') return;
      this.busy = true;
      this.setBusy(true);
      this.error?.classList.add('hide');

      try {
        await cart.add(new FormData(this.form));
      } catch (err) {
        if (this.error) {
          this.error.textContent =
            err?.description || 'That could not be added. Try again.';
          this.error.classList.remove('hide');
        }
      } finally {
        this.busy = false;
        this.setBusy(false);
      }
    }

    setBusy(state) {
      if (!this.button) return;
      this.button.classList.toggle('is-busy', state);
      this.button.toggleAttribute('aria-busy', state);
    }
  }

  customElements.define('product-form', ProductForm);

  /* --- Lazy 3D object ----------------------------------------------------- */
  /* The viewer library is large, so it is never part of the page's initial
     cost: the script is requested the first time this element approaches
     the viewport, and only once per page. */

  const MODEL_VIEWER_SRC =
    'https://cdnjs.cloudflare.com/ajax/libs/model-viewer/4.1.0/model-viewer.min.js';
  let modelViewerLoad = null;

  const loadModelViewer = () => {
    if (modelViewerLoad) return modelViewerLoad;
    modelViewerLoad = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = MODEL_VIEWER_SRC;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return modelViewerLoad;
  };

  class SpinModel extends HTMLElement {
    connectedCallback() {
      if (reduced) {
        /* No autorotate for reduced motion: show the poster instead */
        if (this.dataset.poster) {
          this.innerHTML = `<img src="${this.dataset.poster}" alt="" loading="lazy">`;
          this.setAttribute('data-ready', '');
        }
        return;
      }

      /* In the header the element is on screen from the first paint, so
         waiting for intersection would mean loading the library during the
         critical path. Defer to idle time instead. */
      if (this.dataset.idle === 'true') {
        const start = () => this.mount();
        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 250 });
        } else {
          setTimeout(start, 100);
        }
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          this.mount();
        },
        { rootMargin: '400px' }
      );
      io.observe(this);
    }

    async mount() {
      const src = this.dataset.model;
      if (!src) return;

      const seconds = Number(this.dataset.speed) || 14;
      const degrees = 360 / seconds;
      const signed = this.dataset.direction === 'reverse' ? -degrees : degrees;

      const viewer = document.createElement('model-viewer');
      viewer.setAttribute('src', src);
      viewer.setAttribute('alt', '');
      viewer.setAttribute('auto-rotate', '');
      viewer.setAttribute('auto-rotate-delay', '0');
      viewer.setAttribute('rotation-per-second', `${signed}deg`);
      viewer.setAttribute('interaction-prompt', 'none');
      viewer.setAttribute('disable-zoom', '');
      viewer.setAttribute('disable-tap', '');
      viewer.setAttribute('camera-orbit', '0deg 90deg auto');
      viewer.setAttribute('shadow-intensity', '0');
      viewer.setAttribute('loading', 'lazy');
      if (this.dataset.poster) viewer.setAttribute('poster', this.dataset.poster);

      this.appendChild(viewer);

      try {
        await loadModelViewer();
        this.setAttribute('data-ready', '');
        if (this.dataset.host) {
          this.closest(this.dataset.host)?.setAttribute('data-ready', '');
        }
      } catch {
        /* Library blocked or offline: fall back to the poster alone */
        viewer.remove();
        if (this.dataset.poster) {
          this.innerHTML = `<img src="${this.dataset.poster}" alt="" loading="lazy">`;
          this.setAttribute('data-ready', '');
        }
      }
    }
  }

  customElements.define('spin-model', SpinModel);

  /* --- Discount codes ----------------------------------------------------- */
  /* Shopify's AJAX cart API has no endpoint for discount codes. The supported
     path is the /discount/CODE route, which sets it on the session and
     redirects back. So this is a navigation, not a fetch. */

    const applyDiscount = async () => {
    const input = document.querySelector('[data-discount-input]');
    const button = document.querySelector('[data-discount-apply]');
    const code = input?.value.trim();
    if (!code) {
      input?.focus();
      return;
    }
    if (button) {
      button.disabled = true;
      button.textContent = 'Applying…';
    }
    try {
      /* Applied in the background, so the drawer stays open. */
      await fetch(
        `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`,
        { credentials: 'same-origin' }
      );
      await cart.refresh();
    } catch (err) {
      /* If that fails, use the old method and reopen the cart after the reload. */
      try {
        sessionStorage.setItem('kx-reopen-cart', '1');
      } catch (e) {}
      const back = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${back}`;
      return;
    }
    const again = document.querySelector('[data-discount-apply]');
    if (again) {
      again.disabled = false;
      again.textContent = 'Apply';
    }
  };

  /* After the fallback reload, put the cart back on screen. */
  try {
    if (sessionStorage.getItem('kx-reopen-cart')) {
      sessionStorage.removeItem('kx-reopen-cart');
      customElements
        .whenDefined('kraxie-drawer')
        .then(() => document.getElementById('cart-drawer')?.open());
    }
  } catch (e) {}

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-discount-apply]')) {
      e.preventDefault();
      applyDiscount();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) {
      e.preventDefault();
      applyDiscount();
    }
  });

  /* --- Public surface ----------------------------------------------------- */

  window.Kraxie = Object.assign(window.Kraxie || {}, {
    bus,
    cart,
    lockScroll,
    unlockScroll,
    reduced
  });
})();
